import React, { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Upload, FileCheck2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function ImportDialog({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/obligations-import-template.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "obligo-import-template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Template download failed");
    }
  };

  const upload = async () => {
    if (!file) {
      toast.error("Choose a CSV file first");
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/obligations-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.created > 0) {
        toast.success(`Imported ${data.created} obligation(s)`);
      }
      if (data.errors?.length > 0 && data.created === 0) {
        toast.error(`Import completed with ${data.errors.length} error(s)`);
      }
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setUploading(false);
    }
  };

  const close = () => {
    setFile(null);
    setResult(null);
    onOpenChange(false);
    if (result?.created > 0) onImported?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent data-testid="import-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Import obligations from CSV</DialogTitle>
          <DialogDescription>
            Bulk-onboard obligations from an existing spreadsheet. Unknown categories will be added automatically to your custom list.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-4 text-sm">
            <div className="font-medium mb-1">Need a starting point?</div>
            <p className="text-slate-600 mb-3">
              Download the CSV template with all supported columns and two example rows.
            </p>
            <Button data-testid="download-template-btn" size="sm" variant="outline" onClick={downloadTemplate} className="gap-2">
              <Download className="w-3.5 h-3.5" /> Download template
            </Button>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 block mb-2">Choose CSV file</label>
            <Input
              data-testid="import-file-input"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="cursor-pointer"
            />
            <p className="text-[11px] text-slate-500 mt-2">
              Required columns: name, category, type, amount, frequency, start_date, next_due_date.
              Types: <span className="mono">Recurring · EMI · Loan Repayment · Liability · Compliance/Renewal</span>.
            </p>
          </div>

          {result && (
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-2 text-sm">
                <FileCheck2 className="w-4 h-4 text-emerald-600" />
                <span data-testid="import-result-summary">
                  Imported <b>{result.created}</b> obligation(s)
                  {result.errors?.length > 0 && ` · ${result.errors.length} row error(s)`}
                </span>
              </div>
              {result.errors?.length > 0 && (
                <ul className="max-h-40 overflow-y-auto text-xs divide-y divide-slate-100">
                  {result.errors.slice(0, 25).map((err) => (
                    <li key={err} className="px-4 py-2 flex items-start gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                      <span className="mono text-slate-700">{err}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button data-testid="import-close-btn" variant="outline" onClick={close}>Close</Button>
          <Button data-testid="import-upload-btn" onClick={upload} disabled={uploading || !file} className="bg-blue-700 hover:bg-blue-800 text-white gap-2">
            <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload & import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
